import { Injectable, inject } from '@angular/core';
import { productFiltersNames } from '@clubhub/constants';
import { ProductsFilterService } from '@clubhub/ignite/services/products/products-filter.service';
import { FilterOptionsContext } from '@clubhub/ignite/services/products/products-filter/products-filter.helper';
import {
  affordabilityMetUnknownValue,
  customInitialPeriodFilterName,
  productFilterGroupTitles,
} from '@shared/constants/filter-preference-options';
import {
  ISimpleGridFilterOperation,
  Product,
  ProductLengthExtended,
  ProductTypeString,
  SimpleGridFilterOperator,
} from '@shared/models';
import { ProductFilterPreferenceItem, ProductFilterPreferences } from '@shared/models/filter-preferences';
import { IgniteHelperService } from '@shared/services/ignite.helper';

/** The search form fields a Product search preference seeds, in the form's own value domain. */
interface SearchFormPreferenceValues {
  productLengths: ProductLengthExtended[];
  rateTypes: ProductTypeString[];
  /** Only set when the broker's stored period is Custom, which carries its own month range. */
  customPeriod: { from: number | null; to: number | null } | null;
}

/** A preference that was not applied, and why. Returned rather than swallowed - see the class note. */
interface SkippedPreference {
  filterGroup: string;
  filterName: string;
  reason: 'unavailable' | 'unknown-filter';
}

/** The grid column each grid-backed preference drives, and the product field that says whether it has values. */
const gridBackedGroups = [
  {
    group: productFilterGroupTitles.criteria,
    column: 'lenderOutcomeResult',
    hasValue: (product: Product) => product.lenderOutcomeResult !== undefined,
  },
  {
    group: productFilterGroupTitles.affordabilityMet,
    column: 'lenderAffordabilityMet',
    hasValue: (product: Product) => product.lenderAffordabilityMet !== undefined,
  },
] as const;

/**
 * Applies a broker's resolved Product search preferences to a live search.
 *
 * Three things this deliberately does NOT do:
 *
 * - It does not clear first. `applySavedFilters` opens with `clearFilters()` because a saved search is a complete
 *   replacement; preferences are not. A slot left at "System default" has to keep whatever the tool does by
 *   default, which for Tailored products is "exclude every available item", so this merges on top.
 * - It does not apply a value the current result set cannot offer. Today an unavailable value is applied anyway
 *   and filters the grid to nothing, with no visible control to undo it - worst for Rate type, whose options are
 *   derived from the returned products, so there is no checkbox on screen at all.
 * - It does not fail silently. Both of the underlying setters swallow a bad title - one stores it unvalidated,
 *   the other returns early - so anything skipped is reported back rather than disappearing.
 */
@Injectable({ providedIn: 'root' })
export class ProductPreferencesApplierService {
  private readonly productsFilterService = inject(ProductsFilterService);
  private readonly igniteHelperService = inject(IgniteHelperService);

  /**
   * Applies the sidebar filter groups: Product features and Tailored products.
   *
   * Rate type and Initial product period are not handled here. They are fields on the search form, and seeding
   * the form reaches the sidebar anyway through `ProductsSearchService.mapModelToFilters`.
   */
  public applySidebarFilters(
    preferences: ProductFilterPreferences,
    context: FilterOptionsContext<Product>,
  ): SkippedPreference[] {
    const skipped: SkippedPreference[] = [];

    const sidebarGroups: readonly string[] =
      [productFilterGroupTitles.productFeatures, productFilterGroupTitles.tailoredProducts];
    const items = preferences.filters.filter(item => sidebarGroups.includes(item.filterGroup));

    if (items.length === 0) {
      return skipped;
    }

    // What the panel would render for this result set, which is also what it is able to apply: getFilterOptions
    // already drops anything not visible for the lending type or not present in the products.
    const available = this.availableTitlesByGroup(context);

    for (const item of items) {
      const groupOptions = available.get(item.filterGroup);

      if (!groupOptions) {
        skipped.push({ filterGroup: item.filterGroup, filterName: item.filterName, reason: 'unknown-filter' });
        continue;
      }

      if (!groupOptions.has(item.filterName)) {
        skipped.push({ filterGroup: item.filterGroup, filterName: item.filterName, reason: 'unavailable' });
        continue;
      }

      this.applySidebarItem(item);
    }

    this.productsFilterService.filterChanged.emit();

    return skipped;
  }

  /**
   * Applies the two grid-column preferences: Criteria and Affordability met.
   *
   * These are columns on the results grid rather than sidebar filters, so they go through the grid's own filter
   * API and are gated on the products actually carrying a value. The ticket's own example is this case: with no
   * affordability search run, every product's Affordability met is undefined and a stored "Pass" would empty the
   * grid rather than being ignored.
   */
  public applyGridFilters(
    preferences: ProductFilterPreferences,
    products: readonly Product[],
    setFilter: (filter: ISimpleGridFilterOperation) => void,
  ): SkippedPreference[] {
    const skipped: SkippedPreference[] = [];

    for (const { group, column, hasValue } of gridBackedGroups) {
      const items = preferences.filters.filter(item => item.filterGroup === group && item.isChecked);

      if (items.length === 0) {
        continue;
      }

      if (!products.some(hasValue)) {
        skipped.push(...items.map(item => ({
          filterGroup: item.filterGroup,
          filterName: item.filterName,
          reason: 'unavailable' as const,
        })));
        continue;
      }

      setFilter({
        column,
        op: SimpleGridFilterOperator.In,
        // The empty string is Affordability met's real value for "unknown", so it is a selection like any other.
        value: items.map(item => item.filterName ?? affordabilityMetUnknownValue),
      });
    }

    return skipped;
  }

  /**
   * The two Product search preferences that belong to the search form rather than the results panel: Initial
   * product period and Rate type.
   *
   * Seeding the form is enough for both - `ProductsSearchService.mapModelToFilters` pushes them into the sidebar
   * filters when the search runs, which is why the applier above deliberately ignores these two groups.
   *
   * Both mappings are derived from the form's own option lists rather than written out here. The preference
   * stores a display title for the period ("2 years") and an enum name for the rate type, while the form wants
   * `ProductLengthExtended` and `ProductTypeString` - so a hand-written table would be a third spelling of the
   * same thing, and a rename on either side would silently stop matching.
   */
  public toSearchFormValues(preferences: ProductFilterPreferences): SearchFormPreferenceValues {
    const periodOptions = this.igniteHelperService.productLengthExtendedOptions();
    const rateTypeOptions = this.igniteHelperService.rateTypesOptions;

    const storedFor = (group: string) =>
      preferences.filters.filter(item => item.filterGroup === group && item.isChecked);

    const productLengths = storedFor(productFiltersNames.initialProductPeriod)
      .map(item => periodOptions.find(option => option.label === item.filterName)?.value)
      .filter((value): value is ProductLengthExtended => value !== undefined);

    const rateTypes = storedFor(productFiltersNames.rateType)
      .map(item => rateTypeOptions.find(option => option.value === item.filterName)?.value)
      .filter((value): value is ProductTypeString => value !== undefined);

    // Custom is mutually exclusive with the fixed periods, enforced by a valueChanges rule on the form. Seeding a
    // combination the form would immediately rewrite would leave the screen disagreeing with the stored value.
    if (productLengths.includes(ProductLengthExtended.Custom)) {
      const custom = storedFor(productFiltersNames.initialProductPeriod)
        .find(item => item.filterName === customInitialPeriodFilterName);

      return {
        productLengths: [ProductLengthExtended.Custom],
        rateTypes,
        customPeriod: { from: custom?.fromValue ?? null, to: custom?.toValue ?? null },
      };
    }

    return { productLengths, rateTypes, customPeriod: null };
  }

  /**
   * The sidebar filter groups the broker's firm has locked, by title.
   *
   * Mirrors `FilterPreferenceFilterGroups.LockedBy` on the API, which pairs each group with the flag that locks
   * it so the two cannot be matched up wrongly. Only an explicit false is a lock - true and null both mean the
   * broker may change it.
   */
  public lockedFilterGroups(preferences: ProductFilterPreferences): ReadonlySet<string> {
    const lockedBy: [string, boolean | null][] = [
      [productFilterGroupTitles.rateType, preferences.isRateTypeOverridable],
      [productFilterGroupTitles.initialProductPeriod, preferences.isInitialProductPeriodOverridable],
      [productFilterGroupTitles.productFeatures, preferences.isProductFeaturesOverridable],
      [productFilterGroupTitles.tailoredProducts, preferences.isTailoredProductsOverridable],
      [productFilterGroupTitles.criteria, preferences.isCriteriaOverridable],
      [productFilterGroupTitles.affordabilityMet, preferences.isAffordabilityMetOverridable],
    ];

    return new Set(lockedBy.filter(([, isOverridable]) => isOverridable === false).map(([group]) => group));
  }

  /**
   * Whether the broker may no longer re-sort the results grid by a given column.
   *
   * The grid expresses sorting as clicks on a column header, so the two stored flags do not map one-to-one onto
   * it. `Sort on` locked means no other column may be sorted by at all. `Sort direction` has no control of its
   * own - it is a second click on the same header - so locking it as well can only be honoured by refusing the
   * preferred column too, leaving the grid on the firm's column and direction with nothing to click.
   *
   * A locked `Sort on` with no column resolved (nothing stored, or a stored column this lending type does not
   * show) locks every column, which is the safe reading: the firm has said the broker does not choose the sort.
   */
  public isSortColumnLocked(
    preferences: ProductFilterPreferences,
    columnName: string,
    preferredSortColumn: string | null,
  ): boolean {
    if (preferences.isSortOnOverridable !== false) {
      return false;
    }

    return columnName !== preferredSortColumn || preferences.isSortDirectionOverridable === false;
  }

  /** Every title the panel can currently offer, by group. */
  private availableTitlesByGroup(context: FilterOptionsContext<Product>): Map<string, Set<string>> {
    return new Map(
      this.productsFilterService
        .getFilterOptions(context)
        .map(group => [group.title, new Set(group.items.map(item => item.title))] as const),
    );
  }

  private applySidebarItem(item: ProductFilterPreferenceItem): void {
    if (item.filterGroup === productFilterGroupTitles.tailoredProducts) {
      // Include / exclude rather than a tick: the two flags carry the state, and both false is a deliberate
      // "include in results" rather than the absence of a choice.
      this.productsFilterService.setIncludeExcludeFilter(
        item.filterGroup,
        item.filterName,
        item.isIncluded,
        item.isExcluded,
        true,
        item.checkedSubFilters,
      );

      return;
    }

    if (!item.isChecked) {
      return;
    }

    if (item.numericValue !== null && item.numericValue !== undefined) {
      // A numeric filter with no value matches nothing at runtime, so the value travels with the tick.
      this.productsFilterService.setNumericValueFilter(
        {
          title: item.filterName,
          checked: true,
          included: false,
          excluded: false,
          hasNumericValue: true,
          numericValue: item.numericValue,
        },
        true,
        true,
      );

      return;
    }

    this.productsFilterService.setFilter(item.filterGroup, item.filterName, true, true);
  }
}
